import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import auditService from '../services/auditService';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { FilterIcon, EyeIcon, SettingsIcon } from '../components/Icons';
import './Logs.css';

const emptyFilters = { search: '', admin_username: '', method: '', status: '', target_entity: '', target_id: '', start_date: '', end_date: '' };
const authEventLabels = { login_success: 'Login succeeded', login_failed: 'Login failed', '2fa_required': '2FA required' };
const methodColors = { GET: 'var(--primary)', POST: 'var(--success)', PUT: 'var(--warning)', PATCH: 'var(--warning)', DELETE: 'var(--error)' };
const statusColor = code => code >= 500 ? 'var(--error)' : code >= 400 ? 'var(--warning)' : code >= 200 ? 'var(--success)' : 'var(--text-muted)';
const FilterField = ({ label, children }) => <label className="audit-filter"><span>{label}</span>{children}</label>;

const Logs = () => {
  const { admin, hasPermission } = useAuth();
  const canView = hasPermission('audit_logs', 'view');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [query, setQuery] = useState({ page: 1, limit: 20, filters: emptyFilters });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    if (!canView) return undefined;
    let active = true;
    const fetchLogs = async () => {
      setLoading(true);
      setError('');
      try {
        const params = { ...query.filters, page: query.page, limit: query.limit };
        for (const key of ['start_date', 'end_date']) {
          params[key] = params[key] ? new Date(params[key]).toISOString() : undefined;
        }
        const res = await auditService.getLogs(params);
        if (!res.success) throw new Error(res.message || 'Unable to fetch logs');
        if (active) {
          setLogs(res.data);
          setPagination({ ...res.pagination, pages: Math.max(1, res.pagination.totalPages) });
        }
      } catch (err) {
        if (active) setError(err.response?.data?.message || err.message || 'Unable to fetch logs');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchLogs();
    return () => { active = false; };
  }, [canView, query]);

  useEffect(() => {
    if (!selectedLog) return undefined;
    let active = true;
    setDetail(null);
    setDetailError('');
    auditService.getLogById(selectedLog._id).then(res => {
      if (!res.success) throw new Error(res.message || 'Unable to fetch log details');
      if (active) setDetail(res.data);
    }).catch(err => {
      if (active) setDetailError(err.response?.data?.message || err.message || 'Unable to fetch log details');
    });
    return () => { active = false; };
  }, [selectedLog]);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const applyFilters = event => {
    event.preventDefault();
    if (filters.start_date && filters.end_date && new Date(filters.start_date) > new Date(filters.end_date)) {
      setError('Start time must be before end time.');
      return;
    }
    setQuery(prev => ({ ...prev, page: 1, filters: { ...filters } }));
  };
  const resetFilters = () => {
    setFilters(emptyFilters);
    setQuery(prev => ({ ...prev, page: 1, filters: emptyFilters }));
  };

  if (!canView) return <div className="card audit-message">You do not have permission to view audit logs.</div>;

  return (
    <div className="logs-page">
      <form className="card audit-filters" onSubmit={applyFilters}>
        <div className="audit-filter-grid">
          <FilterField label="Search">
            <input className="input" placeholder="Action, record ID, IP or request ID" value={filters.search} onChange={e => updateFilter('search', e.target.value)} />
          </FilterField>
          <FilterField label="Operator">
            <input className="input" placeholder="Username or login email" value={filters.admin_username} onChange={e => updateFilter('admin_username', e.target.value)} />
          </FilterField>
          <FilterField label="Method">
            <select className="input" value={filters.method} onChange={e => updateFilter('method', e.target.value)}>
              <option value="">All methods</option>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map(method => <option key={method}>{method}</option>)}
            </select>
          </FilterField>
          <FilterField label="Result">
            <select className="input" value={filters.status} onChange={e => updateFilter('status', e.target.value)}>
              <option value="">All results</option>
              <option value="success">Successful (2xx / 3xx)</option>
              <option value="failure">Failed (4xx / 5xx)</option>
              <option value="client_error">Client errors (4xx)</option>
              <option value="server_error">Server errors (5xx)</option>
            </select>
          </FilterField>
          <FilterField label="Entity">
            <input className="input" placeholder="e.g. merchant, tickets, auth" value={filters.target_entity} onChange={e => updateFilter('target_entity', e.target.value)} />
          </FilterField>
          <FilterField label="Record ID">
            <input className="input" placeholder="Exact record ID" value={filters.target_id} onChange={e => updateFilter('target_id', e.target.value)} />
          </FilterField>
          <FilterField label="From">
            <input className="input" type="datetime-local" value={filters.start_date} onChange={e => updateFilter('start_date', e.target.value)} />
          </FilterField>
          <FilterField label="To">
            <input className="input" type="datetime-local" value={filters.end_date} onChange={e => updateFilter('end_date', e.target.value)} />
          </FilterField>
        </div>
        <div className="audit-filter-actions">
          <span className="audit-help">Dates and times use your local timezone.</span>
          {admin?.role === 'super_admin' && <Link className="btn btn-outline" to="/settings"><SettingsIcon /> Settings</Link>}
          <button className="btn btn-outline" type="button" onClick={resetFilters} disabled={loading}>Clear</button>
          <button className="btn btn-primary" type="submit" disabled={loading}><FilterIcon size={16} /> Apply filters</button>
        </div>
      </form>

      {error && <div className="card audit-message audit-error" role="alert">{error}</div>}
      <div className="card audit-results" aria-busy={loading}>
        <div className="audit-table-scroll">
          <table className="table-striped audit-table">
            <thead><tr>{['Timestamp', 'Operator', 'Action', 'Entity', 'IP Address', 'Status', 'Details'].map(title => <th key={title}>{title}</th>)}</tr></thead>
            <tbody>
              {loading && logs.length === 0 ? <tr><td colSpan="7" className="audit-message">Loading logs...</td></tr> : logs.length === 0 ? (
                <tr><td colSpan="7" className="audit-message">No logs match these filters.</td></tr>
              ) : logs.map(log => {
                const parts = log.action.match(/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS) (.*)$/);
                const method = parts?.[1] || 'EVENT';
                return (
                  <tr key={log._id}>
                    <td className="audit-timestamp">{new Date(log.created_at).toLocaleString()}</td>
                    <td><strong>{log.admin_username}</strong><small>{log.admin_role || log.admin_id?.role || ''}</small></td>
                    <td className="audit-action"><strong style={{ color: methodColors[method] || 'var(--text-secondary)' }}>{method}</strong> {parts?.[2] || log.action}</td>
                    <td>{log.target_entity || '—'}<small>{log.target_id}</small></td>
                    <td>{log.ip_address || '—'}</td>
                    <td><strong style={{ color: statusColor(log.details?.statusCode) }}>{log.details?.statusCode || '—'}</strong><small>{authEventLabels[log.details?.auth_event] || ''}</small></td>
                    <td><button className="btn-icon" type="button" onClick={() => { setDetail(null); setDetailError(''); setSelectedLog(log); }} aria-label="View log details" title="View details"><EyeIcon size={18} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={page => setQuery(prev => ({ ...prev, page }))}
          onLimitChange={limit => setQuery(prev => ({ ...prev, page: 1, limit }))} loading={loading} itemName="logs" />
      </div>

      <Modal isOpen={!!selectedLog} onClose={() => setSelectedLog(null)} title="Audit Log Entry" maxWidth="850px">
        {detailError ? <p className="audit-error" role="alert">{detailError}</p> : !detail ? <p>Loading log details...</p> : (
          <>
            <dl className="audit-detail-grid">
              {[
                ['Date & Time', new Date(detail.created_at).toLocaleString()],
                ['Operator', detail.admin_username],
                ['Role at Time of Action', detail.admin_role || '—'],
                ['Action', detail.action],
                ['Target', [detail.target_entity, detail.target_id].filter(Boolean).join(' / ')],
                ['Origin IP', detail.ip_address],
                ['Request ID', detail.request_id],
                ['Status', [detail.details?.statusCode, detail.details?.statusMessage, authEventLabels[detail.details?.auth_event]].filter(Boolean).join(' · ')]
              ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>)}
            </dl>
            <h4>Payload & Metadata</h4>
            <pre className="audit-payload">{JSON.stringify(detail.details, null, 2)}</pre>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Logs;
